/**
 * Argus Live — pure helpers for the 3-pane authoring UI (AL-B3).
 *
 * Pure-logic utilities split out from the route + page components so
 * they can be unit-tested without JSDOM. Mirrors the helper-test pattern
 * the rest of the web package uses (e.g. `mention-parser.ts` /
 * `mention-parser.test.ts` in components/ask-argus).
 *
 * Three responsibilities:
 *
 *   1. Iframe URL construction (centralised so the 3-pane editor + the
 *      preview pane never disagree on query-string shape).
 *   2. Permission-gated affordance helpers — turn raw permission flags
 *      into the UX-relevant booleans the page uses (canEditDashboard,
 *      canPublish, canArchive).
 *   3. Auto-save debounce timing constants + the "should-save" predicate
 *      used by both the Ctrl+S handler and the 30s idle timer.
 *
 * Why pure helpers: every web .test.ts file in this repo is a pure-
 * logic vitest (no JSDOM, no @testing-library). Helpers let us hit a
 * meaningful test count on AL-B3 without adding a JSDOM dependency
 * mid-stream.
 */

import type { ArgusLiveDashboard, ArgusLiveVersion } from './use-argus-live';

// ─── Iframe URL construction ─────────────────────────────────────────────

/**
 * Build the iframe URL for the live preview pane.
 *
 * The AL-A3 endpoint (`packages/api/src/routes/v1/argus-live/iframe.ts`)
 * is mounted at `/api/v1/argus-live/dashboards/:id/iframe` and accepts
 *   - `versionId` (required, UUID) — which version to render
 *   - `part`      (optional, defaults to 'html')
 *
 * We hard-code the path here so the preview pane and edit route can't
 * diverge. If AL-A3 changes the path shape, exactly one file needs to
 * be updated.
 *
 * @example
 *   buildIframeSrc('abc-123', 'ver-456')
 *   // => '/api/v1/argus-live/dashboards/abc-123/iframe?versionId=ver-456'
 */
export function buildIframeSrc(
  dashboardId: string,
  versionId: string,
  part?: 'html' | 'runtime' | 'js' | 'css',
): string {
  if (!dashboardId) throw new Error('dashboardId is required');
  if (!versionId) throw new Error('versionId is required');
  const qs = new URLSearchParams({ versionId });
  if (part && part !== 'html') qs.set('part', part);
  return `/api/v1/argus-live/dashboards/${dashboardId}/iframe?${qs.toString()}`;
}

// ─── Permission-gated affordance helpers ─────────────────────────────────

/**
 * Permission inputs the page reads. Mirrors the five
 * `useHasPermission(action, 'argus_live')` calls the route makes:
 *
 *   - read:argus_live              (gates the route at all)
 *   - write:argus_live             (gates Save Draft + edits)
 *   - lifecycle:unlock:argus_live  (gates Publish + Unarchive)
 *   - lifecycle:lock:argus_live    (gates Archive)
 *   - share:argus_live             (gates Share button + grant management UI)
 *
 * `canShare` is the Layer-1 permission gate; Layer 2 is the per-dashboard
 * admin grant (only an admin grantee — or the owner — can mutate the
 * dashboard's ACL). Both layers checked by `computeShareAffordances`.
 */
export interface ArgusLivePermissionFlags {
  canRead: boolean;
  canWrite: boolean;
  canUnlock: boolean;
  canLock: boolean;
  canShare: boolean;
}

/**
 * UI-level decisions derived from the raw permission flags + the
 * dashboard's effectiveRole (the per-dashboard grant role returned by
 * `GET /dashboards/:id`).
 *
 * Two-layer gate matches ARGUS-LIVE-SPEC §5.5:
 *   - Layer 1: tenant-role-driven permission ("can this user author at
 *     all?") — comes from useHasPermission against
 *     packages/shared/src/schemas/permissions.ts.
 *   - Layer 2: per-dashboard grant role ("can this user edit THIS
 *     specific dashboard?") — comes from the server's effectiveRole on
 *     the dashboard GET response.
 *
 * Both must be true for an action to be enabled.
 */
export interface DashboardAffordances {
  /** Show Save Draft button as enabled. */
  canSaveDraft: boolean;
  /** Show Publish button as enabled. */
  canPublish: boolean;
  /** Show Archive (or Unarchive) action as enabled. */
  canArchive: boolean;
  canUnarchive: boolean;
  /** Show the read-only banner ("you have view-only access"). */
  isReadOnly: boolean;
  /**
   * Show the Share button. Requires BOTH `share:argus_live` permission
   * AND an admin-class grant on this specific dashboard (or implicit
   * owner = admin). Spec §8.2 reserves grant mutation for admins.
   */
  canShare: boolean;
}

/**
 * Compute UI affordances from permission flags + the dashboard's
 * effectiveRole. The owner check is intentionally NOT here — the
 * server already returns `effectiveRole='admin'` for the owner.
 */
export function computeAffordances(
  perms: ArgusLivePermissionFlags,
  dashboard: Pick<ArgusLiveDashboard, 'status'> | null,
  // `operator` (Cockpit Phase 2) ranks between view + edit. For the AUTHORING
  // affordances computed here it behaves as a non-edit role: an operator can
  // execute gated actions in the OPERATE view but cannot save/publish/edit
  // layout, so it correctly falls through every `hasEditGrant` branch below.
  effectiveRole: 'view' | 'operator' | 'edit' | 'admin' | null,
): DashboardAffordances {
  const isArchived = dashboard?.status === 'archived';
  const hasEditGrant = effectiveRole === 'edit' || effectiveRole === 'admin';
  const hasAdminGrant = effectiveRole === 'admin';

  return {
    canSaveDraft: perms.canWrite && hasEditGrant && !isArchived,
    canPublish: perms.canUnlock && hasEditGrant && !isArchived,
    canArchive: perms.canLock && hasAdminGrant && !isArchived,
    canUnarchive: perms.canUnlock && hasAdminGrant && isArchived,
    isReadOnly: !hasEditGrant || isArchived,
    // Share is allowed regardless of archived status — admins may need
    // to revoke a grant on an archived dashboard. Sysadmins (Layer 1
    // `share:argus_live` true via platform_admin role) can always share.
    canShare: perms.canShare && hasAdminGrant,
  };
}

// ─── Share dialog helpers (AL-C5) ────────────────────────────────────────

/**
 * Subject kinds a dashboard grant can target. Mirrors the XOR in the
 * `tenant_dashboard_grants` schema and the discriminator the share
 * dialog uses for its tabs.
 */
export type ArgusLiveGranteeKind = 'user' | 'group' | 'role';

/** Tenant roles accepted by the API's `granteeRole` enum. */
export const ARGUS_LIVE_TENANT_ROLES = [
  'owner',
  'admin',
  'member',
  'viewer',
] as const;
export type ArgusLiveTenantRole = (typeof ARGUS_LIVE_TENANT_ROLES)[number];

/**
 * Grant-tier values accepted by the API's `role` enum. Ordered low → high:
 * view < operator < edit < admin. `operator` (Cockpit Phase 2) = view +
 * (Phase 3) execute/approve gated actions, but NOT edit layout. Mirrors the
 * API `CreateGrantBody` + DASHBOARD_GRANT_ROLES.
 */
export const ARGUS_LIVE_GRANT_ROLES = [
  'view',
  'operator',
  'edit',
  'admin',
] as const;
export type ArgusLiveGrantRoleTier = (typeof ARGUS_LIVE_GRANT_ROLES)[number];

/**
 * Cockpit OPERATE-view predicates over the per-dashboard effective grant role.
 *
 * These drive the OPERATE view's affordances (NOT the editor — see
 * `computeAffordances` for that). The runtime host re-enforces the same gates
 * server-side; these are the usability layer.
 *
 *   - `canOperateRead`  : any non-null grant can OPEN + view a cockpit.
 *   - `canExecuteCockpitActions` : operator | edit | admin can run gated actions.
 *   - `canReachCockpitEditor`    : edit | admin can reach the authoring editor.
 */
export function canOperateRead(
  role: 'view' | 'operator' | 'edit' | 'admin' | null,
): boolean {
  return role != null;
}

export function canExecuteCockpitActions(
  role: 'view' | 'operator' | 'edit' | 'admin' | null,
): boolean {
  return role === 'operator' || role === 'edit' || role === 'admin';
}

export function canReachCockpitEditor(
  role: 'view' | 'operator' | 'edit' | 'admin' | null,
): boolean {
  return role === 'edit' || role === 'admin';
}

/**
 * Pure validator — used by the dialog's "Add grant" Submit button to
 * disable itself for invalid drafts before the network round-trip.
 *
 * Returns `null` on success or an error string suitable for surfacing
 * inline + via toast (matches the ShareFolderDialog UX).
 *
 * Rules (mirrored from `CreateGrantBody` in `routes/.../grants.ts`):
 *   - Exactly one of granteeUserId / granteeGroupId / granteeRole must
 *     be present (XOR enforced server-side).
 *   - `role` must be one of view / edit / admin.
 *   - `expiresAt` (if given) must parse as a Date AND be in the future.
 */
export interface GrantDraft {
  kind: ArgusLiveGranteeKind;
  granteeUserId?: string;
  granteeGroupId?: string;
  granteeRole?: ArgusLiveTenantRole;
  role: ArgusLiveGrantRoleTier;
  expiresAt?: string;
}

export function validateGrantDraft(
  draft: GrantDraft,
  now: Date = new Date(),
): string | null {
  // Kind ↔ grantee field must agree.
  if (draft.kind === 'user') {
    if (!draft.granteeUserId) return 'Pick a user.';
    if (draft.granteeGroupId || draft.granteeRole) {
      return 'A user grant must not specify a group or role.';
    }
  } else if (draft.kind === 'group') {
    if (!draft.granteeGroupId) return 'Pick a group.';
    if (draft.granteeUserId || draft.granteeRole) {
      return 'A group grant must not specify a user or role.';
    }
  } else if (draft.kind === 'role') {
    if (!draft.granteeRole) return 'Pick a tenant role.';
    if (!ARGUS_LIVE_TENANT_ROLES.includes(draft.granteeRole)) {
      return `Invalid role '${draft.granteeRole}'.`;
    }
    if (draft.granteeUserId || draft.granteeGroupId) {
      return 'A role grant must not specify a user or group.';
    }
  } else {
    return `Invalid grantee kind '${(draft as { kind: string }).kind}'.`;
  }

  if (!ARGUS_LIVE_GRANT_ROLES.includes(draft.role)) {
    return `Invalid grant role '${draft.role}'.`;
  }

  if (draft.expiresAt) {
    const parsed = new Date(draft.expiresAt);
    if (Number.isNaN(parsed.getTime())) {
      return 'Expiry date is not a valid datetime.';
    }
    if (parsed.getTime() <= now.getTime()) {
      return 'Expiry must be in the future.';
    }
  }

  return null;
}

/**
 * Convert a UI <input type="datetime-local"> string into a Zod-acceptable
 * ISO-8601 datetime-with-offset. The API's `expiresAt` body field uses
 * `z.string().datetime({ offset: true })`, which rejects plain
 * `YYYY-MM-DDTHH:MM` without an offset. Returns `undefined` for empty
 * strings (no expiry).
 */
export function normalizeExpiresAtForApi(
  raw: string | undefined | null,
): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  // datetime-local has no timezone; treat as local + serialize to ISO
  // with the browser's offset.
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString();
}

/**
 * Bulk-grant input. The share dialog lets an admin select several
 * users / groups at once + apply the same role + expiry. We flatten
 * the selection into a list of {@link GrantDraft}s the mutation runner
 * can iterate over (one POST per draft — the API is idempotent per
 * grantee, so partial failures are safe to retry).
 */
export interface BulkGrantInputs {
  userIds: readonly string[];
  groupIds: readonly string[];
  roles: readonly ArgusLiveTenantRole[];
  role: ArgusLiveGrantRoleTier;
  expiresAt?: string;
}

export function expandBulkGrantInputs(
  inputs: BulkGrantInputs,
): readonly GrantDraft[] {
  const out: GrantDraft[] = [];
  for (const userId of inputs.userIds) {
    out.push({
      kind: 'user',
      granteeUserId: userId,
      role: inputs.role,
      expiresAt: inputs.expiresAt,
    });
  }
  for (const groupId of inputs.groupIds) {
    out.push({
      kind: 'group',
      granteeGroupId: groupId,
      role: inputs.role,
      expiresAt: inputs.expiresAt,
    });
  }
  for (const role of inputs.roles) {
    out.push({
      kind: 'role',
      granteeRole: role,
      role: inputs.role,
      expiresAt: inputs.expiresAt,
    });
  }
  return out;
}

/**
 * Predicate: is a grant currently active (not expired)?
 *
 * Used by the dialog's grant list to dim expired rows + show an
 * "expired" badge. Matches the server-side filter in
 * `effectivePermission()` — a grant past its `expiresAt` is ignored.
 */
export function isGrantActive(
  grant: { expiresAt: string | null },
  now: Date = new Date(),
): boolean {
  if (!grant.expiresAt) return true;
  return new Date(grant.expiresAt).getTime() > now.getTime();
}

/**
 * Human label for a grant row. Pure so the dialog test can exercise
 * the full label-resolution paths.
 *
 * Resolution order (matches ShareFolderDialog):
 *   user  → "<firstName lastName> (<email>)" if found, else email,
 *           else the raw subject ID.
 *   group → group name if found, else raw ID.
 *   role  → "Role: <role>".
 */
export function describeGrantSubject(
  grant: Pick<
    ArgusLiveGrantSubject,
    'granteeUserId' | 'granteeGroupId' | 'granteeRole'
  >,
  users: readonly { id: string; email: string; firstName?: string | null; lastName?: string | null }[] = [],
  groups: readonly { id: string; name: string }[] = [],
): string {
  if (grant.granteeUserId) {
    const u = users.find((x) => x.id === grant.granteeUserId);
    if (u) {
      const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
      return name ? `${name} (${u.email})` : u.email;
    }
    return grant.granteeUserId;
  }
  if (grant.granteeGroupId) {
    const g = groups.find((x) => x.id === grant.granteeGroupId);
    return g ? g.name : grant.granteeGroupId;
  }
  if (grant.granteeRole) {
    return `Role: ${grant.granteeRole}`;
  }
  return 'Unknown grantee';
}

/** Minimal shape a grant row needs for `describeGrantSubject`. */
export interface ArgusLiveGrantSubject {
  granteeUserId: string | null;
  granteeGroupId: string | null;
  granteeRole: string | null;
}

// ─── Auto-save timing ────────────────────────────────────────────────────

/** Idle-timer duration before auto-saving (30s per AL-B3 task spec). */
export const AUTO_SAVE_IDLE_MS = 30_000;

/**
 * Pure predicate: should we trigger an auto-save? Used by the page's
 * effect so the test surface is decoupled from setTimeout.
 *
 * Returns false when:
 *   - The author is not permitted to write.
 *   - The dashboard is read-only / archived.
 *   - The draft buffer is identical to the last-saved snapshot.
 *   - There is already a save in flight.
 */
export interface AutoSaveInputs {
  draft: {
    html: string;
    js: string;
    css: string;
    revLabel: string;
    dataBindingsJson: string;
  };
  lastSaved: {
    html: string;
    js: string;
    css: string;
    revLabel: string;
    dataBindingsJson: string;
  } | null;
  canSaveDraft: boolean;
  isSavingNow: boolean;
}

export function shouldAutoSave(inputs: AutoSaveInputs): boolean {
  if (!inputs.canSaveDraft || inputs.isSavingNow) return false;
  const ls = inputs.lastSaved;
  if (!ls) {
    return (
      inputs.draft.html.length > 0 ||
      inputs.draft.js.length > 0 ||
      inputs.draft.css.length > 0 ||
      inputs.draft.dataBindingsJson.trim() !== '[]'
    );
  }
  return (
    inputs.draft.html !== ls.html ||
    inputs.draft.js !== ls.js ||
    inputs.draft.css !== ls.css ||
    inputs.draft.revLabel !== ls.revLabel ||
    inputs.draft.dataBindingsJson !== ls.dataBindingsJson
  );
}

// ─── Version label helper ────────────────────────────────────────────────

/**
 * Format a version for the dropdown ("v1.0 — 2 hours ago"). Pure +
 * memoizable. We don't pull in a date library; the relative-time
 * formatter is intentionally simple (we only need minutes / hours /
 * days resolution for the dropdown label).
 */
export function formatVersionLabel(
  version: Pick<ArgusLiveVersion, 'revLabel' | 'createdAt'>,
  now: Date = new Date(),
): string {
  const created = new Date(version.createdAt);
  const diffMs = Math.max(0, now.getTime() - created.getTime());
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr / 24);

  let rel: string;
  if (diffMin < 1) rel = 'just now';
  else if (diffMin < 60) rel = `${diffMin}m ago`;
  else if (diffHr < 24) rel = `${diffHr}h ago`;
  else if (diffDay < 30) rel = `${diffDay}d ago`;
  else rel = created.toISOString().slice(0, 10);

  return `${version.revLabel} — ${rel}`;
}

// ─── Default draft contents ──────────────────────────────────────────────

/**
 * Initial-state for a brand-new dashboard's draft. Mirrors the AL-A3
 * iframe runtime expectations: HTML body fragment (no <html>/<head>),
 * separate CSS + JS files. The runtime template injects these into the
 * sandboxed iframe.
 */
export const DEFAULT_DRAFT_HTML =
  '<div id="argus-live-root">\n  <h1>Hello from your new dashboard</h1>\n  <p>Open the chat on the left and ask Argus to build something.</p>\n</div>\n';

export const DEFAULT_DRAFT_CSS =
  '#argus-live-root {\n  font-family: system-ui, sans-serif;\n  padding: 1rem;\n  color: var(--argus-live-text, #111);\n}\n#argus-live-root h1 { font-size: 1.25rem; font-weight: 600; }\n';

export const DEFAULT_DRAFT_JS =
  '// Dashboard JS runs in a sandboxed iframe.\n// `argusLive` is the runtime bridge (postMessage to host).\n// See ARGUS-LIVE-SPEC §4 for the protocol shape.\n';

// ─── Code-tab type ───────────────────────────────────────────────────────

export type CodeTab = 'html' | 'js' | 'css' | 'bindings';

/**
 * Map a CodeTab to the Monaco language id. Centralised so the tab
 * switcher + the editor mount agree.
 */
export function monacoLanguageForTab(
  tab: CodeTab,
): 'html' | 'javascript' | 'css' | 'json' {
  switch (tab) {
    case 'html':
      return 'html';
    case 'js':
      return 'javascript';
    case 'css':
      return 'css';
    case 'bindings':
      return 'json';
  }
}

/**
 * Stable, human-editable JSON for dashboard data bindings.
 *
 * The API already accepts `dataBindings` on draft-save and publish, but the
 * editor previously had no way to preserve or edit that manifest, so a normal
 * UI save/publish could drop bindings that were created by an authoring agent
 * or imported version. Keep this helper pure so source-free tests can lock the
 * contract without mounting Monaco.
 */
export function formatDataBindingsJson(
  bindings: unknown[] | null | undefined,
): string {
  return JSON.stringify(Array.isArray(bindings) ? bindings : [], null, 2);
}

export function parseDataBindingsJson(raw: string): unknown[] {
  const trimmed = raw.trim();
  if (!trimmed) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch (err) {
    throw new Error(`Data bindings JSON is invalid: ${(err as Error).message}`);
  }
  if (!Array.isArray(parsed)) {
    throw new Error('Data bindings must be a JSON array.');
  }
  const errors = parsed
    .map((item, index) => validateDataBindingShape(item, index))
    .filter((message): message is string => typeof message === 'string');
  if (errors.length > 0) {
    throw new Error(`Data bindings are invalid: ${errors.join('; ')}`);
  }
  return parsed;
}

const DATA_BINDING_ID_RE = /^[A-Za-z0-9._:-]+$/;
const DATA_BINDING_TOOL_RE =
  /^(iq|ext|forge)\.[a-zA-Z0-9_-]+\.[a-zA-Z_][a-zA-Z0-9_]*$/;
const DATA_BINDING_TABLE_RE = /^[a-z][a-z0-9_]*$/;
const DATA_BINDING_REFRESH_MODES = new Set([
  'manual',
  'mount',
  'interval',
  'event',
  'subscribe',
]);

/**
 * Cockpit Phase 1 (§2.B) — the seven binding source kinds. Mirrors
 * `BindingSourceKindSchema` in @argusai/shared and the server's
 * `BindingSourceKind` in data-fetch.service.ts. Kept as a hand-rolled
 * set to match this validator's no-Zod style.
 */
const DATA_BINDING_SOURCE_KINDS = new Set([
  'library',
  'mcp_tool',
  'connection',
  'workflow',
  'tables',
  'cockpit_state',
  'action',
]);
/** The four gateway-dispatch kinds (require the top-level toolSlug). */
const DATA_BINDING_GATEWAY_SOURCE_KINDS = new Set([
  'library',
  'mcp_tool',
  'connection',
  'workflow',
]);
const DATA_BINDING_SOURCE_ALLOWED_KEYS = new Set([
  'kind',
  'tableSlug',
  'actionId',
  // Cockpit Phase 3 action target + gating (§2.C).
  'actionKind',
  'toolSlug',
  'workflowId',
  'approvalGating',
  'buttonLabel',
]);
/** Cockpit Phase 3 — action target kinds + gating vocabularies. */
const DATA_BINDING_ACTION_KINDS = new Set(['mcp_tool', 'workflow']);
const DATA_BINDING_ACTION_GATINGS = new Set([
  'read_only',
  'approval_required',
  'auto_execute',
]);
const DATA_BINDING_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DATA_BINDING_ALLOWED_KEYS = new Set([
  'id',
  'name',
  'source',
  'toolSlug',
  'params',
  'refreshMode',
  'refreshIntervalMs',
  'eventTopic',
  'classificationHint',
  'userOverridableParams',
  'tables',
]);

function validateDataBindingShape(item: unknown, index: number): string | null {
  const path = `binding ${index + 1}`;
  if (!item || typeof item !== 'object' || Array.isArray(item)) {
    return `${path} must be an object`;
  }
  const b = item as Record<string, unknown>;
  const extraKey = Object.keys(b).find(
    (key) => !DATA_BINDING_ALLOWED_KEYS.has(key),
  );
  if (extraKey) {
    return `${path} has unsupported field '${extraKey}'`;
  }
  const id = b.id;
  if (
    typeof id !== 'string' ||
    id.trim().length === 0 ||
    id.length > 120 ||
    !DATA_BINDING_ID_RE.test(id)
  ) {
    return `${path} needs id (1-120 chars: letters, numbers, '.', '_', '-', ':')`;
  }

  // ─── Cockpit Phase 1 source discriminator (§2.B) ──────────────────
  // OPTIONAL. Absent = legacy `mcp_tool` (toolSlug required). When
  // present, validate the kind enum + the conditional tableSlug/actionId
  // and use the kind to decide whether toolSlug is required. Mirrors the
  // shared-schema superRefine + the server's resolveBindingSource.
  let sourceKind: string | undefined;
  const source = b.source;
  if (source !== undefined) {
    if (!source || typeof source !== 'object' || Array.isArray(source)) {
      return `${path} source must be an object`;
    }
    const s = source as Record<string, unknown>;
    const extraSourceKey = Object.keys(s).find(
      (key) => !DATA_BINDING_SOURCE_ALLOWED_KEYS.has(key),
    );
    if (extraSourceKey) {
      return `${path} source has unsupported field '${extraSourceKey}'`;
    }
    if (
      typeof s.kind !== 'string' ||
      !DATA_BINDING_SOURCE_KINDS.has(s.kind)
    ) {
      return `${path} source.kind must be one of library, mcp_tool, connection, workflow, tables, cockpit_state, action`;
    }
    sourceKind = s.kind;
    const ACTION_ONLY_KEYS = [
      'actionId',
      'actionKind',
      'toolSlug',
      'workflowId',
      'approvalGating',
      'buttonLabel',
    ] as const;
    if (sourceKind === 'tables' || sourceKind === 'cockpit_state') {
      if (
        typeof s.tableSlug !== 'string' ||
        s.tableSlug.length === 0 ||
        s.tableSlug.length > 120 ||
        !DATA_BINDING_TABLE_RE.test(s.tableSlug)
      ) {
        return `${path} source.kind='${sourceKind}' requires a tableSlug (lowercase table slug)`;
      }
      for (const k of ACTION_ONLY_KEYS) {
        if (s[k] !== undefined) {
          return `${path} source.kind='${sourceKind}' must not carry '${k}'`;
        }
      }
    } else if (sourceKind === 'action') {
      if (
        typeof s.actionId !== 'string' ||
        s.actionId.length === 0 ||
        s.actionId.length > 120
      ) {
        return `${path} source.kind='action' requires an actionId (1-120 chars)`;
      }
      if (s.tableSlug !== undefined) {
        return `${path} source.kind='action' must not carry a tableSlug`;
      }
      // Cockpit Phase 3 — action target + gating. actionKind/approvalGating
      // are REQUIRED for a USABLE action; when actionKind is present, validate
      // the matching target (mcp_tool ⇒ toolSlug, workflow ⇒ workflowId).
      if (
        s.approvalGating !== undefined &&
        (typeof s.approvalGating !== 'string' ||
          !DATA_BINDING_ACTION_GATINGS.has(s.approvalGating))
      ) {
        return `${path} source.approvalGating must be read_only, approval_required, or auto_execute`;
      }
      if (
        s.buttonLabel !== undefined &&
        (typeof s.buttonLabel !== 'string' ||
          s.buttonLabel.length === 0 ||
          s.buttonLabel.length > 120)
      ) {
        return `${path} source.buttonLabel must be a non-empty string up to 120 chars`;
      }
      if (s.actionKind !== undefined) {
        if (
          typeof s.actionKind !== 'string' ||
          !DATA_BINDING_ACTION_KINDS.has(s.actionKind)
        ) {
          return `${path} source.actionKind must be mcp_tool or workflow`;
        }
        if (s.actionKind === 'mcp_tool') {
          if (
            typeof s.toolSlug !== 'string' ||
            !DATA_BINDING_TOOL_RE.test(s.toolSlug)
          ) {
            return `${path} source.actionKind='mcp_tool' requires a namespaced toolSlug`;
          }
          if (s.workflowId !== undefined) {
            return `${path} source.actionKind='mcp_tool' must not carry a workflowId`;
          }
        } else {
          // workflow
          if (
            typeof s.workflowId !== 'string' ||
            !DATA_BINDING_UUID_RE.test(s.workflowId)
          ) {
            return `${path} source.actionKind='workflow' requires a workflowId UUID`;
          }
          if (s.toolSlug !== undefined) {
            return `${path} source.actionKind='workflow' must not carry a toolSlug`;
          }
        }
      } else {
        // No actionKind ⇒ Phase-1 minimal action; the target fields must be
        // absent (the binding is not yet executable, executor rejects).
        if (s.toolSlug !== undefined || s.workflowId !== undefined) {
          return `${path} source.kind='action' without actionKind must not carry toolSlug/workflowId`;
        }
      }
    } else {
      // Gateway kinds: library / mcp_tool / connection / workflow.
      if (s.tableSlug !== undefined) {
        return `${path} source.kind='${sourceKind}' is a gateway read; it must not carry a tableSlug`;
      }
      for (const k of ACTION_ONLY_KEYS) {
        if (s[k] !== undefined) {
          return `${path} source.kind='${sourceKind}' is a gateway read; it must not carry '${k}'`;
        }
      }
    }
  }

  // toolSlug is conditionally-required (Cockpit Phase 1): required when
  // there is no source OR the source is a gateway kind; not required for
  // tables / cockpit_state / action. When present it must be regex-valid
  // regardless of kind.
  const needsToolSlug =
    sourceKind === undefined ||
    DATA_BINDING_GATEWAY_SOURCE_KINDS.has(sourceKind);
  if (b.toolSlug !== undefined) {
    if (
      typeof b.toolSlug !== 'string' ||
      !DATA_BINDING_TOOL_RE.test(b.toolSlug)
    ) {
      return `${path} needs toolSlug like iq.tables.query or ext.platform.getStatus`;
    }
  } else if (needsToolSlug) {
    return `${path} needs toolSlug like iq.tables.query or ext.platform.getStatus`;
  }

  if (
    typeof b.name !== 'string' ||
    b.name.trim().length === 0 ||
    b.name.length > 200
  ) {
    return `${path} name must be a non-empty string up to 200 chars`;
  }
  if (
    typeof b.refreshMode !== 'string' ||
    !DATA_BINDING_REFRESH_MODES.has(b.refreshMode)
  ) {
    return `${path} refreshMode must be manual, mount, interval, event, or subscribe`;
  }
  if (
    b.refreshIntervalMs !== undefined &&
    b.refreshIntervalMs !== null &&
    (typeof b.refreshIntervalMs !== 'number' ||
      !Number.isInteger(b.refreshIntervalMs) ||
      b.refreshIntervalMs < 5_000 ||
      b.refreshIntervalMs > 86_400_000)
  ) {
    return `${path} refreshIntervalMs must be 5000-86400000`;
  }
  if (
    b.params !== undefined &&
    (!b.params || typeof b.params !== 'object' || Array.isArray(b.params))
  ) {
    return `${path} params must be an object`;
  }
  if (
    b.refreshMode === 'interval' &&
    typeof b.refreshIntervalMs !== 'number'
  ) {
    return `${path} refreshMode=interval requires refreshIntervalMs`;
  }
  if (
    b.eventTopic !== undefined &&
    (typeof b.eventTopic !== 'string' ||
      b.eventTopic.trim().length === 0 ||
      b.eventTopic.length > 120)
  ) {
    return `${path} eventTopic must be a non-empty string up to 120 chars`;
  }
  if (
    b.refreshMode === 'event' &&
    (typeof b.eventTopic !== 'string' || b.eventTopic.trim().length === 0)
  ) {
    return `${path} refreshMode=event requires eventTopic`;
  }
  if (b.tables !== undefined) {
    if (!Array.isArray(b.tables)) return `${path} tables must be an array`;
    if (b.tables.length > 10) {
      return `${path} tables must contain at most 10 slugs`;
    }
    const badTable = b.tables.find(
      (table) =>
        typeof table !== 'string' ||
        table.length === 0 ||
        table.length > 120 ||
        !DATA_BINDING_TABLE_RE.test(table),
    );
    if (badTable !== undefined) {
      return `${path} tables must contain lowercase table slugs`;
    }
  }
  if (
    b.refreshMode === 'subscribe' &&
    (!Array.isArray(b.tables) || b.tables.length === 0)
  ) {
    return `${path} refreshMode=subscribe requires tables`;
  }
  if (b.userOverridableParams !== undefined) {
    if (!Array.isArray(b.userOverridableParams)) {
      return `${path} userOverridableParams must be an array`;
    }
    const badParam = b.userOverridableParams.find(
      (param) =>
        typeof param !== 'string' || param.length === 0 || param.length > 120,
    );
    if (badParam !== undefined) {
      return `${path} userOverridableParams must contain non-empty strings`;
    }
  }
  const hint = b.classificationHint;
  if (hint !== undefined) {
    if (!hint || typeof hint !== 'object' || Array.isArray(hint)) {
      return `${path} classificationHint must be an object`;
    }
    const h = hint as Record<string, unknown>;
    if (
      h.sensitivity !== undefined &&
      h.sensitivity !== 'unclassified' &&
      h.sensitivity !== 'cui' &&
      h.sensitivity !== 'restricted'
    ) {
      return `${path} classificationHint.sensitivity is invalid`;
    }
    if (
      h.exportControl !== undefined &&
      h.exportControl !== 'none' &&
      h.exportControl !== 'itar' &&
      h.exportControl !== 'ear'
    ) {
      return `${path} classificationHint.exportControl is invalid`;
    }
  }
  return null;
}

// ─── Status pill tone mapping ────────────────────────────────────────────

export type DashboardStatusTone =
  | 'healthy'
  | 'warning'
  | 'destructive'
  | 'info'
  | 'muted';

export function statusToneFor(status: 'draft' | 'published' | 'archived'): DashboardStatusTone {
  switch (status) {
    case 'published':
      return 'healthy';
    case 'draft':
      return 'info';
    case 'archived':
      return 'muted';
  }
}

// ─── Slug generation ─────────────────────────────────────────────────────

/**
 * Convert a human-readable title into a valid Argus Live slug.
 *
 * Server-side validation in `CreateDashboardBody`
 * (packages/api/src/routes/v1/argus-live/dashboards.ts) accepts:
 *
 *   /^[a-z0-9][a-z0-9._-]*$/    1..120 chars
 *
 * We:
 *   - lowercase
 *   - replace whitespace + any disallowed char with `-`
 *   - collapse repeated separators
 *   - trim leading/trailing separators
 *   - cap at 120 chars
 *
 * The "new dashboard" form auto-derives the slug from the title using
 * this helper until the user manually types into the slug field.
 */
export function titleToSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-._]+/, '')
    .replace(/[-._]+$/, '')
    .slice(0, 120);
}

/**
 * Slug validation predicate — mirrors the server-side regex so the UI
 * can disable the Create button before sending the request.
 */
export function isValidArgusLiveSlug(slug: string): boolean {
  return slug.length >= 1 && slug.length <= 120 && /^[a-z0-9][a-z0-9._-]*$/.test(slug);
}
